import { escapeReferenceToken } from '@criteria/json-pointer'
import {
  BreadcrumbItem,
  BreadcrumbLabel,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Heading,
  Input,
  Label,
  Legend,
  Switch,
  SwitchField,
  SwitchGroup,
  Textarea
} from '@criteria-labs/react'
import { Service } from '@criteria-labs/docs-kit'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { PageFormBody, PageFormFooter } from '../PageForm'
import { InspectorArticle, InspectorBody, InspectorHeader } from '../article'
import { AddSecurityScheme, SecurityScheme, SecuritySchemeRef } from '../changes/security-scheme'
import { toUniqueProposedName } from '../toUniqueProposedName'
import { useDispatchChange } from '../useDispatchChange'
import { watch } from '@rollup/browser'
import { messsageFromError } from '../util/messageFromError'
import { useRouter } from '../router'

type Inputs = {
  securitySchemeName: string
  implicitEnabled: boolean
  implicit: {
    authorizationUrl: string
    refreshUrl?: string
    scopes: string
  }
  passwordEnabled: boolean
  password: {
    tokenUrl: string
    refreshUrl?: string
    scopes: string
  }
  clientCredentialsEnabled: boolean
  clientCredentials: {
    tokenUrl: string
    refreshUrl?: string
    scopes: string
  }
  authorizationCodeEnabled: boolean
  authorizationCode: {
    authorizationUrl: string
    tokenUrl: string
    refreshUrl?: string
    scopes: string
  }
  description?: string
}

export function NewOAuth2Auth({ service }: { service: Service }) {
  const { push } = useRouter()

  const defaultIdentifier = toUniqueProposedName(
    'OAuth2Auth',
    Object.keys(service.openAPI.components?.securitySchemes ?? {})
  )

  const dispatchChange = useDispatchChange()
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
    control
  } = useForm<Inputs>({
    defaultValues: {
      securitySchemeName: defaultIdentifier
    }
  })

  const onSubmit: SubmitHandler<Inputs> = ({ securitySchemeName, description, implicitEnabled, implicit }) => {
    try {
      const ref: SecuritySchemeRef = `#/components/securitySchemes/${escapeReferenceToken(securitySchemeName)}`
      const value: SecurityScheme = {
        type: 'oauth2',
        flows: {
          implicit: implicitEnabled
            ? {
                authorizationUrl: implicit.authorizationUrl,
                refreshUrl: implicit.refreshUrl,
                scopes: {}
              }
            : undefined
        },
        description: description && description.length > 0 ? description : undefined
      }
      dispatchChange(new AddSecurityScheme(ref, value))
      push('/')
    } catch (error) {
      setError('root', { message: messsageFromError(error) ?? 'Could not create security scheme' })
    }
  }

  const watchedImplicitEnabled = watch('implicitEnabled')
  const watchedPasswordEnabled = watch('passwordEnabled')
  const watchedClientCredentialsEnabled = watch('clientCredentialsEnabled')
  const watchedAuthorizationCodeEnabled = watch('authorizationCodeEnabled')

  return (
    <InspectorArticle>
      <InspectorHeader className="space-y-3">
        <div className="flex items-center justify-between">
          <BreadcrumbList className="min-h-10">
            <BreadcrumbItem onClick={() => push('/')}>
              <BreadcrumbLabel>API</BreadcrumbLabel>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLabel>New OAuth 2.0 Auth</BreadcrumbLabel>
            </BreadcrumbItem>
          </BreadcrumbList>
          <Button aria-label="Close" plain onClick={() => push('/')}>
            <XMarkIcon />
          </Button>
        </div>
        <Heading>Create a new OAuth 2.0 Authentication Mechanism</Heading>
      </InspectorHeader>
      <InspectorBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors.root && <ErrorMessage className="mt-3">{errors.root.message}</ErrorMessage>}
          <PageFormBody className="space-y-8">
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Security scheme name</Label>
                  <Input {...register('securitySchemeName', { required: true })} />
                  <Description>
                    A key that will identify the security scheme object in the OpenAPI document.
                  </Description>
                  {errors.securitySchemeName?.type === 'required' && (
                    <ErrorMessage>Component name is required</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Description</Label>
                  <Textarea {...register('description')} />
                </Field>
              </FieldGroup>
            </Fieldset>
            <Fieldset>
              <Legend className="flex items-center gap-x-3">
                <span>Configure implicit flow</span>
                <Controller
                  name="implicitEnabled"
                  control={control}
                  render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                />
              </Legend>
              {watchedImplicitEnabled && (
                <FieldGroup>
                  <Field>
                    <Label>Authorization URL</Label>
                    <Input {...register('implicit.authorizationUrl', { required: true })} />
                    {errors.implicit?.authorizationUrl?.type === 'required' && (
                      <ErrorMessage>Authorization URL is required</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Refresh URL</Label>
                    <Input {...register('implicit.refreshUrl')} />
                  </Field>
                  {/* <Field>
                    <Label>Scopes</Label>
                    <Input {...register('implicit.scopes')} />
                  </Field> */}
                </FieldGroup>
              )}
            </Fieldset>
            <Fieldset>
              <Legend className="flex items-center gap-x-3">
                <span>Configure password flow</span>
                <Controller
                  name="passwordEnabled"
                  control={control}
                  render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                />
              </Legend>
              {watchedPasswordEnabled && (
                <FieldGroup>
                  <Field>
                    <Label>Token URL</Label>
                    <Input {...register('password.tokenUrl', { required: true })} />
                    {errors.password?.tokenUrl?.type === 'required' && (
                      <ErrorMessage>Token URL is required</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Refresh URL</Label>
                    <Input {...register('password.refreshUrl')} />
                  </Field>
                  {/* <Field>
                    <Label>Scopes</Label>
                    <Input {...register('password.scopes')} />
                  </Field> */}
                </FieldGroup>
              )}
            </Fieldset>
            <Fieldset>
              <Legend className="flex items-center gap-x-3">
                <span>Configure client credentials flow</span>
                <Controller
                  name="clientCredentialsEnabled"
                  control={control}
                  render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                />
              </Legend>
              {watchedClientCredentialsEnabled && (
                <FieldGroup>
                  <Field>
                    <Label>Token URL</Label>
                    <Input {...register('clientCredentials.tokenUrl', { required: true })} />
                    {errors.clientCredentials?.tokenUrl?.type === 'required' && (
                      <ErrorMessage>Token URL is required</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Refresh URL</Label>
                    <Input {...register('clientCredentials.refreshUrl')} />
                  </Field>
                  {/* <Field>
                    <Label>Scopes</Label>
                    <Input {...register('clientCredentials.scopes')} />
                  </Field> */}
                </FieldGroup>
              )}
            </Fieldset>
            <Fieldset>
              <Legend className="flex items-center gap-x-3">
                <span>Configure authorization code flow</span>
                <Controller
                  name="authorizationCodeEnabled"
                  control={control}
                  render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                />
              </Legend>
              {watchedAuthorizationCodeEnabled && (
                <FieldGroup>
                  <Field>
                    <Label>Authorization URL</Label>
                    <Input {...register('authorizationCode.authorizationUrl', { required: true })} />
                    {errors.authorizationCode?.tokenUrl?.type === 'required' && (
                      <ErrorMessage>Authorization URL is required</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Token URL</Label>
                    <Input {...register('authorizationCode.tokenUrl', { required: true })} />
                    {errors.authorizationCode?.tokenUrl?.type === 'required' && (
                      <ErrorMessage>Token URL is required</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Refresh URL</Label>
                    <Input {...register('authorizationCode.refreshUrl')} />
                  </Field>
                  {/* <Field>
                    <Label>Scopes</Label>
                    <Input {...register('authorizationCode.scopes')} />
                  </Field> */}
                </FieldGroup>
              )}
            </Fieldset>
          </PageFormBody>
          <PageFormFooter>
            <Button type="submit" color="indigo">
              Create OAuth 2.0 Configuration
            </Button>
          </PageFormFooter>
        </form>
      </InspectorBody>
    </InspectorArticle>
  )
}
